import { IEditorNode } from '../Admin/ArnoTop/editorModel';
import { IRichParagraph } from '../Admin/ArnoTop/richTextModel';
import {
  IDinoDeedItem,
  IDinoDeedSection,
} from '../hooks/reactQuerys/DeedGenerationQueries';

/**
 * Die Urkunde im Dialog »In DiNo erstellen« (#1245, Teil 48) — ohne Oberfläche, testbar.
 *
 * Jeder Abschnitt wird in der Leseansicht gezeigt und dort an Ort und Stelle bearbeitet, mit
 * demselben Editor wie in der Verwaltung. Dafür werden seine Absätze zu Textknoten (eine Folge
 * von Absätzen = ein Element) und seine Tabellen zu Tabellenknoten.
 *
 * Wer eine Frage anders beantwortet, bekommt einen neu gerechneten Plan. Von Hand geänderte
 * Abschnitte werden dabei **nicht** überschrieben — die Regel wie beim Aktualisieren in Word:
 * keine stille Überschreibung manueller Arbeit. Hat sich der Plan für einen solchen Abschnitt
 * geändert, steht das dabei, und »Neu übernehmen« ist eine ausdrückliche Entscheidung.
 */

export interface IDeedSectionState {
  /** Der Abschnitt, wie ihn der Plan zuletzt geliefert hat. */
  section: IDinoDeedSection;
  /** Was im Dialog steht und in die Datei geht. */
  nodes: IEditorNode[];
  edited: boolean;
  /** Von Hand geändert — und der Plan sieht inzwischen etwas anderes vor. */
  planChanged: boolean;
}

const stable = (value: unknown): string =>
  JSON.stringify(value, (_key, inner) =>
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.keys(inner)
          .sort()
          .reduce<Record<string, unknown>>((sorted, key) => {
            sorted[key] = (inner as Record<string, unknown>)[key];
            return sorted;
          }, {})
      : inner
  );

/** Absätze und Tabellen eines Abschnitts als Knoten für Leseansicht und Editor. */
export const sectionNodes = (items: IDinoDeedItem[]): IEditorNode[] => {
  const nodes: IEditorNode[] = [];
  let paragraphs: IRichParagraph[] = [];
  const flush = () => {
    if (paragraphs.length > 0) {
      nodes.push({ type: 'text', paragraphs });
      paragraphs = [];
    }
  };
  items.forEach(item => {
    if (item.kind === 'table') {
      flush();
      nodes.push({
        type: 'table',
        rows: item.rows as IEditorNode['rows'],
        widths: item.widths as IEditorNode['widths'],
        borders: item.borders as IEditorNode['borders'],
      });
    } else {
      paragraphs.push(item as unknown as IRichParagraph);
    }
  });
  flush();
  return nodes;
};

/** Umkehrung — was an den Server zurückgeht. */
export const sectionItems = (nodes: IEditorNode[]): IDinoDeedItem[] =>
  nodes.flatMap((node): IDinoDeedItem[] => {
    if (node.type === 'table') {
      return [
        {
          kind: 'table',
          rows: node.rows ?? [],
          widths: node.widths,
          borders: node.borders,
        } as IDinoDeedItem,
      ];
    }
    return (node.paragraphs ?? []).map(
      paragraph =>
        ({ ...paragraph, kind: 'paragraph' }) as unknown as IDinoDeedItem
    );
  });

export const freshState = (section: IDinoDeedSection): IDeedSectionState => ({
  section,
  nodes: sectionNodes(section.items),
  edited: false,
  planChanged: false,
});

/**
 * Neuer Plan nach einer Antwort: unberührte Abschnitte kommen neu, von Hand geänderte bleiben.
 * Liefert auch die geänderten Abschnitte, die der neue Plan gar nicht mehr vorsieht — sie fallen
 * weg, aber nicht still.
 */
export const mergeRerun = (
  previous: IDeedSectionState[],
  sections: IDinoDeedSection[]
): { states: IDeedSectionState[]; droppedEdited: string[] } => {
  const byBlock = new Map(
    previous.map(state => [state.section.blockId, state])
  );
  const states = sections.map(section => {
    const before = byBlock.get(section.blockId);
    if (!before?.edited) {
      return freshState(section);
    }
    const changed = stable(before.section.items) !== stable(section.items);
    return {
      section,
      nodes: before.nodes,
      edited: true,
      planChanged: before.planChanged || changed,
    };
  });
  const kept = new Set(sections.map(section => section.blockId));
  const droppedEdited = previous
    .filter(state => state.edited && !kept.has(state.section.blockId))
    .map(state => state.section.label);
  return { states, droppedEdited };
};

/** Was an `/arno/dino-deeds/docx` geht. */
export const sectionsForFile = (
  states: IDeedSectionState[]
): IDinoDeedSection[] =>
  states.map(state => ({
    ...state.section,
    items: sectionItems(state.nodes),
  }));
